import {
    useMemo,
    useState,
} from "react";
import {
    Avatar,
    Badge,
    HStack,
    Select,
    Table,
    Text,
    VStack,
    createListCollection,
} from "@chakra-ui/react";

import DataTable from "../../../components/DataTable";
import EmptyState from "../../../components/EmptyState";

const dateRanges =
    createListCollection({
        items: [
            {
                label: "Last 30 Days",
                value: "30",
            },
            {
                label: "Last 90 Days",
                value: "90",
            },
            {
                label: "This Year",
                value: "year",
            },
        ],
    });

const statusFilters =
    createListCollection({
        items: [
            {
                label: "All Statuses",
                value: "All",
            },
            {
                label: "Paid",
                value: "Paid",
            },
            {
                label: "Pending",
                value: "Pending",
            },
            {
                label: "Processing",
                value: "Processing",
            },
            {
                label: "Failed",
                value: "Failed",
            },
        ],
    });

const columns = [
    {
        key: "reference",
        label: "Payment Reference",
    },
    {
        key: "period",
        label: "Payout Period",
    },
    {
        key: "amount",
        label: "Amount",
    },
    {
        key: "paid_at",
        label: "Paid Date",
    },
    {
        key: "status",
        label: "Status",
    },
];

const statusColor = {
    Paid: "green",
    Pending: "orange",
    Processing: "blue",
    Failed: "red",
};

const CommissionTable = ({
    payouts = [],
}) => {
     const [
        selectedStatus,
        setSelectedStatus,
    ] = useState("All");

    const [
        selectedRange,
        setSelectedRange,
    ] = useState("30");
const visiblePayouts =
    useMemo(() => {
        let result =
            payouts.filter(
                payout =>
                    Number(
                        payout.amount ||
                            0
                    ) > 0
            );

        // Status filter
        if (
            selectedStatus !==
            "All"
        ) {
            result =
                result.filter(
                    payout =>
                        payout.status ===
                        selectedStatus
                );
        }

        // Date filter
        const now =
            new Date();

        if (
            selectedRange ===
            "30"
        ) {
            result =
                result.filter(
                    payout => {
                        if (
                            !payout.paid_at
                        )
                            return true;

                        const diff =
                            (
                                now -
                                new Date(
                                    payout.paid_at
                                )
                            ) /
                            (
                                1000 *
                                60 *
                                60 *
                                24
                            );

                        return (
                            diff <=
                            30
                        );
                    }
                );
        }

        if (
            selectedRange ===
            "90"
        ) {
            result =
                result.filter(
                    payout => {
                        if (
                            !payout.paid_at
                        )
                            return true;

                        const diff =
                            (
                                now -
                                new Date(
                                    payout.paid_at
                                )
                            ) /
                            (
                                1000 *
                                60 *
                                60 *
                                24
                            );

                        return (
                            diff <=
                            90
                        );
                    }
                );
        }

        if (
            selectedRange ===
            "year"
        ) {
            result =
                result.filter(
                    payout => {
                        if (
                            !payout.paid_at
                        )
                            return true;

                        return (
                            new Date(
                                payout.paid_at
                            ).getFullYear() ===
                            now.getFullYear()
                        );
                    }
                );
        }

        return result;
    }, [
        payouts,
        selectedStatus,
        selectedRange,
    ]);
    return (
        <DataTable
            title="Commission History"
            total={
               visiblePayouts.length
            }
            data={visiblePayouts}
            columns={columns}
            pageSize={10}
            filters={[]}
            emptyComponent={
                <EmptyState
                    imageSize="180px"
                    title="No Commissions"
                    message="You haven't earned any commissions yet. Your commission history will appear here as your recruited drivers reach milestones and complete deliveries."
                    py={12}
                />
            }
            rightAction={
                <HStack     flexDir={{
        base: "column",
        md: "row",
    }}
    align={{
        base: "stretch",
        md: "center",
    }}
    gap={3}
    w={{
        base: "100%",
        md: "auto",
    }}>
                   <Select.Root
                    collection={
                        dateRanges
                    }
                    value={[
                        selectedRange
                    ]}
                    onValueChange={(
                        details
                    ) =>
                        setSelectedRange(
                            details.value[0]
                        )
                    }
                >
                        <Select.Trigger  w={{
        base: "100%",
        md: "170px",
    }}>
                            <Select.ValueText placeholder="Last 30 Days" />
                        </Select.Trigger>

                        <Select.Content>
                            {dateRanges.items.map(
                                (
                                    item
                                ) => (
                                    <Select.Item
                                        key={
                                            item.value
                                        }
                                        item={
                                            item
                                        }
                                    >
                                        {
                                            item.label
                                        }
                                    </Select.Item>
                                )
                            )}
                        </Select.Content>
                    </Select.Root>

                  <Select.Root
                        collection={
                            statusFilters
                        }
                        value={[
                            selectedStatus
                        ]}
                        onValueChange={(
                            details
                        ) =>
                            setSelectedStatus(
                                details.value[0]
                            )
                        }
                    >
                        <Select.Trigger  w={{
        base: "100%",
        md: "170px",
    }}>
                            <Select.ValueText placeholder="All Statuses" />
                        </Select.Trigger>

                        <Select.Content>
                            {statusFilters.items.map(
                                (
                                    item
                                ) => (
                                    <Select.Item
                                        key={
                                            item.value
                                        }
                                        item={
                                            item
                                        }
                                    >
                                        {
                                            item.label
                                        }
                                    </Select.Item>
                                )
                            )}
                        </Select.Content>
                    </Select.Root>
                </HStack>
            }
            renderRow={(
                item
            ) => (
                <Table.Row
                    key={
                        item.id
                    }
                >
                    <Table.Cell>
                        <HStack>
                            <Avatar.Root
                                size="sm"
                                bg="#E8EEFF"
                                color="#2453FF"
                            >
                                <Avatar.Fallback>
                                    P
                                </Avatar.Fallback>
                            </Avatar.Root>

                            <VStack
                                align="start"
                                gap={0}
                            >
                                <Text fontWeight="600">
                                    {
                                        item.payment_reference
                                    }
                                </Text>

                               <Text
                                    fontSize="xs"
                                    color="gray.500"
                                >
                                    {
                                        item.payment_method ||
                                        item.payout_period
                                    }
                                </Text>
                            </VStack>
                        </HStack>
                    </Table.Cell>

                    <Table.Cell>
                        {
                            item.payout_period
                        }
                    </Table.Cell>

                    <Table.Cell>
                        <Text
                            fontWeight="700"
                            color="#071D54"
                        >
                            P
                            {Number(
                                item.amount
                            ).toFixed(
                                2
                            )}
                        </Text>
                    </Table.Cell>

                    <Table.Cell>
                        {item.paid_at
                            ? new Date(
                                  item.paid_at
                              ).toLocaleDateString()
                            : "-"}
                    </Table.Cell>

                    <Table.Cell>
                        <Badge
                            colorPalette={
                                statusColor[
                                    item
                                        .status
                                ] ||
                                "gray"
                            }
                            borderRadius="full"
                            px={3}
                        >
                            {
                                item.status
                            }
                        </Badge>
                    </Table.Cell>
                </Table.Row>
            )}
        />
    );
};

export default CommissionTable;